import {
  ChangeDetectionStrategy,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { LocationInitialUrl } from '@freelancer/location';
import * as Rx from 'rxjs';
import { debounceTime, filter, map, startWith, take } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { getCLS, getFCP, getFID, getLCP, Metric } from 'web-vitals';
import { Tracking } from './tracking.service';

interface LargestContentfulPaint {
  element?: Element;
  url?: string;
}

interface LayoutShift extends PerformanceEntry {
  sources: LayoutShiftAttribution[];
  value: number;
}

interface LayoutShiftAttribution {
  node?: Element;
}

@Component({
  selector: 'fl-core-web-vitals-tracking',
  template: ` <ng-container></ng-container> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CoreWebVitalsTrackingComponent implements OnDestroy, OnInit {
  coreWebVitalsSubscription?: Rx.Subscription;
  hasReportedInitial = false;
  spaSessionId = uuidv4();

  constructor(
    private ngZone: NgZone,
    private t: Tracking,
    private locationInitialUrl: LocationInitialUrl, // private swUpdate: SwUpdate,
  ) {}

  ngOnInit() {
    const isPwaPromise =
      window.navigator && window.navigator.serviceWorker
        ? window.navigator.serviceWorker
            .getRegistration('/')
            .then(registration => registration?.active?.state === 'activated')
        : Promise.resolve(false);

    this.ngZone.runOutsideAngular(async () => {
      const lcp$ = new Rx.Observable<Metric>(observer => {
        // Reports final on `keydown`, `pointerdown`, `scroll`
        // or `visibilitychange` becomes `hidden`
        // Note that this won't report anything if page is hidden
        // before API initializes
        getLCP((metric: Metric) => {
          observer.next(metric);
          observer.complete();
        });
      });

      const fid$ = new Rx.Observable<Metric>(observer => {
        // Reports final on `keydown`, `pointerdown`
        // Note that this won't report anything if page is hidden
        // before API initializes
        getFID((metric: Metric) => {
          observer.next(metric);
          observer.complete();
        });
      });

      const fcp$ = new Rx.Observable<Metric>(observer => {
        // Reports final on `keydown`, `pointerdown`
        // Note that this won't report anything if page is hidden
        // before API initializes
        getFCP((metric: Metric) => {
          observer.next(metric);
          observer.complete();
        });
      });

      const cls$ = new Rx.Observable<Metric>(observer => {
        // Report all changes
        getCLS((metric: Metric) => {
          observer.next(metric);
        }, true);
      });

      // Fallback if user did not interact with page before page is hidden
      const fidOrHidden$ = Rx.merge(
        fid$,
        Rx.fromEvent(document, 'visibilitychange').pipe(
          filter(() => document.visibilityState === 'hidden'),
          map(() => undefined),
          take(1),
        ),
      ).pipe(take(1));

      // Wait for all three core web vitals before tracking on initial page load
      this.coreWebVitalsSubscription = Rx.combineLatest([
        fidOrHidden$,
        // Initial value for LCP and CLS needed for unsupported browsers
        lcp$.pipe(startWith(undefined)),
        cls$.pipe(
          startWith(undefined),
          // Can fire frequently for highly dynamic views
          debounceTime(500),
        ),
        fcp$.pipe(startWith(undefined)),
      ]).subscribe(async ([fid, lcp, cls, fcp]) => {
        // Don't track if we have no metrics, it's possible
        // if page is loaded in the background
        if (!fid && !lcp && !cls && !fcp) {
          return;
        }

        // Report FCP, LCP and FID in one event
        const hasInitialMetrics = fid && fcp && lcp;
        if (!hasInitialMetrics && !cls) {
          return;
        }

        this.t.track('core_web_vitals_timings', {
          fid:
            this.hasReportedInitial || !hasInitialMetrics
              ? undefined
              : fid?.delta, // in ms
          lcp:
            this.hasReportedInitial || !hasInitialMetrics
              ? undefined
              : lcp?.delta, // in ms
          fcp:
            this.hasReportedInitial || !hasInitialMetrics
              ? undefined
              : fcp?.delta,
          cls: cls?.value, // take current value since it changes over time
          lcpElement: this.hasReportedInitial
            ? undefined
            : lcp
            ? this.getLcpElement(lcp)
            : undefined,
          clsElements: cls ? this.getClsElements(cls) : undefined,
          isPwa: await isPwaPromise,
          initialUrl: this.locationInitialUrl.getInitialUrl(),
          spaSessionId: this.spaSessionId,
        });

        // Report FCP, LCP and FID once
        if (hasInitialMetrics) {
          this.hasReportedInitial = true;
        }
      });
    });
  }

  private getLcpElement(metric: Metric) {
    // get the latest LCP element
    const entry = metric.entries[
      metric.entries.length - 1
    ] as LargestContentfulPaint;
    const element: Element | undefined = entry?.element;
    return element ? element.outerHTML?.substr(0, 100) : entry.url;
  }

  private getClsElements(metric: Metric) {
    const entries = metric.entries as LayoutShift[];
    // Since we limit the payload size, we prioritize worst offenders
    entries.sort((a, b) => b.value - a.value);
    return entries
      .map((entry: LayoutShift) => {
        const sources = entry.sources.map((source: any) => {
          const element: HTMLElement = source.node;
          return element?.outerHTML?.substr(0, 100);
        });
        return `${entry.value} ${sources.join(' ')}`;
      })
      .join(' ')
      .substr(0, 1000);
  }

  ngOnDestroy() {
    if (this.coreWebVitalsSubscription) {
      this.coreWebVitalsSubscription.unsubscribe();
    }
  }
}
